import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import LineChart from '../../atoms/LineChart/LineChart';
import ChartNoDataState from '../../atoms/ChartNoDataState/ChartNoDataState';
import Slider from '../../atoms/Slider/Slider';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { EnhancedLineChartProps } from './EnhancedLineChart.types';
import { ChartConfig } from '../../../utils/BaseChart/BaseChartOptions';
import { formatNumWithKMB, CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';
import { getCategoryAxisLabelTransforms } from '../../../utils/chartAxisLabelTransforms';
import { computeSingleAxisDomain, computeZeroAlignedDomains } from '../../../utils/chartAxisAlignment';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { transformVizDataToLineChart } from '../../../utils/transformVizData';
import { findFirstAvailableFamilyIndex } from '../../../utils/chartMetricAvailability';
import { useChartFilters } from '../EnhancedBarChart/useChartFilters';
import {
    getFirstEnabledZOptionValue,
    isZLevelAllSelected,
    zAxisSelectTagRender,
} from '../EnhancedBarChart/zAxisFilterSelectTag';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';
import {
    buildY1GroupedOptions,
    buildY2GroupedOptions,
    chartFilterDropdownRender,
    getSelectableOptionValues,
    getYAxisSelectAllValues,
} from '../EnhancedBarChart/chartFilterDropdownRender';
import ChartLegendOverflow from '../ChartLegendOverflow/ChartLegendOverflow';
import { buildYZSeriesKey, matchLongestMetricLabel } from '../../../utils/chartSeriesKey';

const LINE_FIXED_CATEGORY_STRIDE_PX = 72;
const LINE_CHART_WRAPPER_HORIZONTAL_PADDING = 40;
const LINE_PRIMARY_AXIS_WIDTH_ESTIMATE = 80;
const LINE_SECONDARY_AXIS_WIDTH_ESTIMATE = 64;
const LINE_Y_AXIS_DOMAIN_PADDING = 0.05;

const EnhancedLineChart: React.FC<EnhancedLineChartProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        data: propData,
        xAxisKey: propXAxisKey,
        xAxisTitle: propXAxisTitle,
        yAxisLabel: propYAxisLabel,
        yAxisTitle: propYAxisTitle = 'Values',
        lineConfigs: propLineConfigs,
        filters = [],
        showFilters = true,
        legend: propLegend,
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onPointClick,
        onTrackChartEvent,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        className = '',
        chartClassName = '',
    } = props;

    const fixedSpacingV2 = Boolean((props as EnhancedLineChartProps & { fixedSpacingV2?: boolean }).fixedSpacingV2);
    const xAxisTypeToken = vizData?.xAxisType?.[0]?.trim();
    const xAxisScrollKind = xAxisTypeToken?.toLowerCase();
    const scrollModeFromViz =
        vizData && (xAxisScrollKind === 'category' || xAxisScrollKind === 'time') ? xAxisScrollKind : null;

    const showSlider = fixedSpacingV2
        ? false
        : props.showSlider !== undefined
          ? props.showSlider
          : scrollModeFromViz === 'category'
            ? false
            : scrollModeFromViz === 'time'
              ? true
              : true;

    const showScrollbar = fixedSpacingV2
        ? true
        : props.showScrollbar !== undefined
          ? props.showScrollbar
          : scrollModeFromViz === 'category'
            ? true
            : scrollModeFromViz === 'time'
              ? false
              : false;

    const scrollbarVisibleCount = props.scrollbarVisibleCount ?? 10;

    const theme = useTheme();

    // Use chart filters hook if vizData is provided
    const chartFilters = useChartFilters(vizData);

    /** Secondary axis only applies when there are multiple Y metric families (viz_data). */
    const showSecondaryAxisFilter = useMemo(() => {
        const yOpts = chartFilters.filterOptions?.y;
        if (!yOpts?.length) return false;
        return new Set(yOpts.map((y) => y.familyIndex)).size > 1;
    }, [chartFilters.filterOptions?.y]);

    // Transform viz_data if provided, otherwise use props directly
    const chartConfig = useMemo(() => {
        if (vizData) {
            // Use filtered data and selected metrics
            const filteredVizData = {
                ...vizData,
                data: chartFilters.filteredData,
            };

            // Ensure we have at least one metric selected for rendering
            // If no metrics are selected, default to all metrics in the first family that has data
            let selectedMetrics = chartFilters.selectedMetrics;
            if (
                (!selectedMetrics.y1 || selectedMetrics.y1.length === 0) &&
                (!selectedMetrics.y2 || selectedMetrics.y2.length === 0) &&
                vizData.yAxisTitle.length > 0
            ) {
                // Select all metrics from the first family that has at least one non-null value
                const familyIndex = findFirstAvailableFamilyIndex(vizData.data, vizData.yAxisTitle);
                const firstFamily = vizData.yAxisTitle[familyIndex];
                const defaultY1Metrics = firstFamily
                    ? firstFamily.map((_: string, index: number) => `${familyIndex}_${index}`)
                    : ['0_0'];
                selectedMetrics = {
                    y1: defaultY1Metrics,
                    y2: [],
                };
            }

            const transformed = transformVizDataToLineChart(filteredVizData, selectedMetrics);
            return {
                title: transformed.title,
                data: transformed.data,
                xAxisKey: transformed.xAxisKey,
                yAxisLabel: transformed.yAxisLabel,
                y1LineConfigs: transformed.y1LineConfigs,
                y2LineConfigs: transformed.y2LineConfigs,
                zAxisKey: transformed.zAxisKey,
                legend: {
                    y1: transformed.y1LineConfigs.map((config) => ({
                        label: config.label || config.dataKey,
                        color: config.color,
                    })),
                    y2: transformed.y2LineConfigs.map((config) => ({
                        label: config.label || config.dataKey,
                        color: config.color,
                    })),
                },
            };
        } else {
            // Use props directly (legacy format)
            if (!propData || !propXAxisKey || !propLineConfigs) {
                throw new Error('Either vizData or (data, xAxisKey, lineConfigs) must be provided');
            }
            return {
                title: propTitle || '',
                data: propData,
                xAxisKey: propXAxisKey,
                yAxisLabel: propYAxisLabel,
                y1LineConfigs: propLineConfigs,
                y2LineConfigs: [],
                zAxisKey: undefined,
                legend: propLegend
                    ? Array.isArray(propLegend)
                        ? { y1: propLegend, y2: [] }
                        : propLegend
                    : {
                          y1: propLineConfigs.map((config) => ({
                              label: config.label || config.dataKey,
                              color: config.color,
                          })),
                          y2: [],
                      },
            };
        }
    }, [
        vizData,
        propTitle,
        propData,
        propXAxisKey,
        propYAxisLabel,
        propLineConfigs,
        propLegend,
        chartFilters.filteredData,
        chartFilters.selectedMetrics,
    ]);

    const { title, data, xAxisKey, yAxisLabel, y1LineConfigs, y2LineConfigs } = chartConfig;

    const uniqueCategories = useMemo(
        () => Array.from(new Set(data.map((d: any) => d[xAxisKey]))),
        [data, xAxisKey],
    );
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);
    const [scrollOffset, setScrollOffset] = useState(0);
    const chartRef = useRef<Chart | null>(null);
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const [chartWrapperWidth, setChartWrapperWidth] = useState(0);
    const SCROLL_THROTTLE_MS = 150;

    useEffect(() => {
        if (!fixedSpacingV2) return undefined;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return undefined;

        const updateWidth = () => {
            setChartWrapperWidth(wrapper.getBoundingClientRect().width);
        };
        updateWidth();

        if (typeof ResizeObserver === 'undefined') return undefined;

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);
        return () => resizeObserver.disconnect();
    }, [fixedSpacingV2]);

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
        setScrollOffset(0);
        scrollOffsetRef.current = 0;
    }, [totalCategories]);

    const sliderFilteredCategories = useMemo(
        () => new Set(uniqueCategories.slice(sliderRange[0], sliderRange[1] + 1)),
        [uniqueCategories, sliderRange],
    );

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;

    // Combine all line configs
    const allLineConfigs = [...y1LineConfigs, ...y2LineConfigs];
    const y1DataKeys = new Set(y1LineConfigs.map((config) => config.dataKey));

    // Transform data to include series field for multiple lines
    // Each unique combination of metric + Z values creates a separate series
    const transformedData = data.flatMap((item: Record<string, any>) =>
        allLineConfigs.map((config) => {
            const metricLabel = config.label || config.dataKey;
            // If Z-axis values exist, combine metric label with Z series key to create unique series
            // This ensures each Z selection creates a separate line
            const seriesKey = item.zValues?.length
                ? buildYZSeriesKey(metricLabel, item.zValues as string[])
                : metricLabel;
            
            return {
                ...item,
                x: item[xAxisKey],
                y: item[config.dataKey],
                y_type: seriesKey, // Use combined series key for unique series identification
                metricLabel: metricLabel, // Keep original metric label for reference
                axisGroup: y1DataKeys.has(config.dataKey) ? 'y1' : 'y2',
                originalValue: item[config.dataKey],
            };
        }),
    );

    // Generate colors for all unique series (metric + Z combinations)
    // Get all unique series keys from transformed data
    const uniqueSeries = Array.from(new Set(transformedData.map((d: any) => d.y_type))).sort();
    const metricLabelsForMatch = allLineConfigs.map((c) => c.label || c.dataKey);
    const seriesColors = uniqueSeries.map((series: string, index: number) => {
        const baseMetric = matchLongestMetricLabel(series, metricLabelsForMatch);
        const baseConfig = baseMetric ? allLineConfigs.find((c) => (c.label || c.dataKey) === baseMetric) : undefined;
        if (baseConfig && series === baseMetric) {
            return baseConfig.color;
        }
        const DEFAULT_COLORS = [
            '#5B50D1', '#DBC7A4', '#4ECDC4', '#FF6B6B', '#95E1D3',
            '#F38181', '#AA96DA', '#FCBAD3', '#A8D8EA', '#FFD93D',
        ];
        return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    });
    const seriesColorMap = new Map(uniqueSeries.map((series: string, i: number) => [series, seriesColors[i]]));

    const displayLegendItems = uniqueSeries.map((series) => ({
        key: series,
        label: series,
        color: seriesColorMap.get(series) || '#5B50D1',
    }));

    const propLegendFlat = useMemo(() => {
        if (propLegend === undefined) return undefined;
        return Array.isArray(propLegend) ? propLegend : [...propLegend.y1, ...propLegend.y2];
    }, [propLegend]);

    const effectiveLegendItems =
        !vizData && propLegendFlat !== undefined
            ? propLegendFlat.length === 0
                ? []
                : propLegendFlat.map((item, index) => ({
                      key: `${item.label}-${index}`,
                      label: item.label,
                      color: item.color,
                  }))
            : displayLegendItems;

    let y1MinMax: { min: number; max: number } | undefined;
    let y2MinMax: { min: number; max: number } | undefined;

    if (y1LineConfigs.length > 0) {
        const y1Values = transformedData
            .filter((d: any) => d.axisGroup === 'y1')
            .map((d: any) => d.y)
            .filter((v: any) => v != null && !isNaN(v));
        if (y1Values.length > 0) {
            y1MinMax = { min: Math.min(...y1Values), max: Math.max(...y1Values) };
        }
    }

    if (y2LineConfigs.length > 0) {
        const y2Values = transformedData
            .filter((d: any) => d.axisGroup === 'y2')
            .map((d: any) => d.y)
            .filter((v: any) => v != null && !isNaN(v));
        if (y2Values.length > 0) {
            y2MinMax = { min: Math.min(...y2Values), max: Math.max(...y2Values) };
        }
    }

    let alignedDomains: { y1: [number, number]; y2: [number, number] } | undefined;
    if (y1MinMax && y2MinMax) {
        alignedDomains = computeZeroAlignedDomains(
            y1MinMax.min, y1MinMax.max,
            y2MinMax.min, y2MinMax.max,
            0.05,
        );
    }

    // Create line configs
    const linesConfig: ChartConfig[] = [];

    /**
     * `scrollbarVisibleCount` is a budget on the *total* visible points. Each x-axis
     * category renders one point per series (metric × Z), so the effective number of
     * categories the scrollbar window exposes is the budget divided by series count.
     * This keeps line density roughly constant as users add/remove metrics or Z-series
     * instead of doubling marks when a second metric is selected.
     */
    const pointsPerCategory = Math.max(1, uniqueSeries.length);
    const estimatedPlotWidth =
        chartWrapperWidth -
        LINE_CHART_WRAPPER_HORIZONTAL_PADDING -
        LINE_PRIMARY_AXIS_WIDTH_ESTIMATE -
        (y2LineConfigs.length > 0 ? LINE_SECONDARY_AXIS_WIDTH_ESTIMATE : 0);
    const v2VisibleCategoryCount =
        estimatedPlotWidth > 0
            ? Math.max(1, Math.floor(estimatedPlotWidth / LINE_FIXED_CATEGORY_STRIDE_PX))
            : Math.max(1, scrollbarVisibleCount);
    const effectiveScrollbarVisibleCount = fixedSpacingV2
        ? v2VisibleCategoryCount
        : Math.max(1, Math.floor(scrollbarVisibleCount / pointsPerCategory));
    const needsScroll = showScrollbar && filteredCategoryCount > effectiveScrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - effectiveScrollbarVisibleCount);
    const effectiveScrollOffset = Math.min(scrollOffset, maxScrollOffset);
    const visibleWindowStartIndex = needsScroll ? sliderRange[0] + effectiveScrollOffset : sliderRange[0];
    const visibleWindowEndIndex = needsScroll
        ? Math.min(visibleWindowStartIndex + effectiveScrollbarVisibleCount - 1, sliderRange[1])
        : sliderRange[1];
    const visibleWindowCategories = useMemo(
        () => new Set(uniqueCategories.slice(visibleWindowStartIndex, visibleWindowEndIndex + 1)),
        [uniqueCategories, visibleWindowStartIndex, visibleWindowEndIndex],
    );

    const scrollWindowCategories = fixedSpacingV2 ? visibleWindowCategories : sliderFilteredCategories;
    const y1AxisDomain =
        alignedDomains?.y1 ??
        (y1MinMax ? computeSingleAxisDomain(y1MinMax.min, y1MinMax.max, LINE_Y_AXIS_DOMAIN_PADDING) : undefined);

    // Y1 lines (left axis)
    if (y1LineConfigs.length > 0) {
        const y1Data = transformedData.filter((d: any) => d.axisGroup === 'y1');
        const y1DataForChart = showScrollbar
            ? y1Data.filter((d: any) => scrollWindowCategories.has(d.x))
            : y1Data;
        linesConfig.push({
            encode: {
                x: 'x',
                y: 'y',
                color: 'y_type',
                series: 'y_type',
            },
            axis: {
                x: {
                    title: propXAxisTitle || false,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    /**
                     * Wrap at the rendered band step, then let G2 ellipsise against the labels' real
                     * measured bounds so no two collide. Pre-truncating in a formatter instead would
                     * discard the full category name, which `attachAxisLabelHoverTooltip` reads back
                     * off the label shape to show on hover. `labelAuto*` flags are absent on purpose
                     * — a non-empty `transform` makes G2 ignore them.
                     */
                    transform: getCategoryAxisLabelTransforms(
                        fixedSpacingV2 ? { tickSpacePx: LINE_FIXED_CATEGORY_STRIDE_PX } : {},
                    ),
                },
                y: {
                    position: 'left',
                    title: propYAxisTitle,
                    labelFormatter: (datum: any) => formatNumWithKMB(datum),
                    grid: true,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            additionalConfig: {
                legend: {
                    color: {
                        position: 'top',
                        layout: { justifyContent: 'center' },
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                    },
                },
                ...(!fixedSpacingV2 && showScrollbar
                    ? {
                          scrollbar: {
                              x: {
                                  ratio: Math.min(
                                      1,
                                      effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount),
                                  ),
                                  trackSize: 8,
                                  thumbFill: '#5B50D1',
                                  thumbFillOpacity: 0.45,
                                  trackFill: '#f0f0f0',
                                  trackFillOpacity: 1,
                                  isRound: true,
                              },
                          },
                      }
                    : !fixedSpacingV2 && showSlider
                      ? {
                            slider: {
                                x: {
                                    selectionFill: '#F7F5FF',
                                    selectionFillOpacity: 1,
                                },
                            },
                        }
                      : {}),
            },
            style: { lineWidth: 2 },
            scale: {
                ...(y1AxisDomain && { y: { domain: y1AxisDomain } }),
                color: {
                    type: 'ordinal',
                    range: uniqueSeries
                        .filter((series: string) => 
                            transformedData.some((d: any) => d.y_type === series && d.axisGroup === 'y1')
                        )
                        .map((series: string) => seriesColorMap.get(series) || '#5B50D1'),
                },
            },
            tooltip: {
                title: (d: any) => String(d.x ?? ''),
                items: [
                    (d: any) => ({
                        name: d.y_type || d.metricLabel || 'Value',
                        value: formatNumWithKMB(Number(d.y)),
                        color: seriesColorMap.get(d.y_type) || '#5B50D1',
                    }),
                ],
            },
            data: y1DataForChart,
        });
    }

    if (y2LineConfigs.length > 0 && y2MinMax) {
        const y2Data = transformedData.filter((d: any) => d.axisGroup === 'y2');
        const y2DataForChart = showScrollbar
            ? y2Data.filter((d: any) => scrollWindowCategories.has(d.x))
            : y2Data;
        const y2AxisDomain: [number, number] = alignedDomains
            ? alignedDomains.y2
            : [y2MinMax.min, y2MinMax.max];

        linesConfig.push({
            encode: {
                x: 'x',
                y: 'y',
                color: 'y_type',
                series: 'y_type',
            },
            axis: {
                x: {
                    title: false,
                },
                y: {
                    position: 'right',
                    title: null,
                    labelFormatter: (datum: any) => formatNumWithKMB(datum),
                    grid: false,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            style: { lineWidth: 2 },
            scale: {
                y: {
                    independent: true,
                    domain: y2AxisDomain,
                },
                color: {
                    independent: true,
                    type: 'ordinal',
                    range: uniqueSeries
                        .filter((series: string) => 
                            transformedData.some((d: any) => d.y_type === series && d.axisGroup === 'y2')
                        )
                        .map((series: string) => seriesColorMap.get(series) || '#5B50D1'),
                },
            },
            tooltip: {
                title: (d: any) => String(d.x ?? ''),
                items: [
                    (d: any) => ({
                        name: d.y_type || d.metricLabel || 'Value',
                        value: formatNumWithKMB(Number(d.y)),
                        color: seriesColorMap.get(d.y_type) || '#5B50D1',
                    }),
                ],
            },
            data: y2DataForChart,
        });
    }

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...(theme as { components?: object }).components,
                Select: {
                    ...((theme as { components?: { Select?: object } })?.components?.Select ?? {}),
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            if (!needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;
            setScrollOffset(next);

            if (fixedSpacingV2) return;

            const chart = chartRef.current;
            if (!chart) return;

            const absStart = sliderRange[0] + next;
            const absEnd = Math.min(absStart + effectiveScrollbarVisibleCount - 1, sliderRange[1]);
            const startCat = uniqueCategories[absStart];
            const endCat = uniqueCategories[absEnd];

            chart.emit('scrollbarX:filter', {
                data: { selection: [[startCat, endCat], undefined] },
            });
        },
        [needsScroll, maxScrollOffset, fixedSpacingV2, sliderRange, effectiveScrollbarVisibleCount, uniqueCategories],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    const chartTitleForTracking = vizData?.chartTitle ?? propTitle;
    const chartTypeForTracking = vizData?.chart_type ?? 'Line';

    const trackChartEvent = onTrackChartEvent;

    const scrollbarThumbRatio = needsScroll
        ? Math.min(1, effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount))
        : 1;
    const scrollbarThumbWidthPercent = scrollbarThumbRatio * 100;
    const scrollbarThumbLeftPercent =
        maxScrollOffset > 0 ? (effectiveScrollOffset / maxScrollOffset) * (100 - scrollbarThumbWidthPercent) : 0;

    const handleScrollbarPointer = useCallback(
        (clientX: number) => {
            const track = scrollbarTrackRef.current;
            if (!track || maxScrollOffset <= 0) return;
            const rect = track.getBoundingClientRect();
            const thumbWidthPx = rect.width * scrollbarThumbRatio;
            const availableTrackPx = Math.max(1, rect.width - thumbWidthPx);
            const leftPx = Math.max(0, Math.min(availableTrackPx, clientX - rect.left - thumbWidthPx / 2));
            const nextOffset = (leftPx / availableTrackPx) * maxScrollOffset;
            const prev = scrollOffsetRef.current;
            scrollToOffset(nextOffset);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: nextOffset > prev ? 'forward' : 'backward',
            });
        },
        [maxScrollOffset, scrollbarThumbRatio, scrollToOffset, trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleScrollbarPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    const handleScrollbarPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if ((event.buttons & 1) !== 1) return;
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    const trackFilterValueChange = useCallback(
        (filter_name: string, prevValues: unknown[], nextValues: unknown[]) => {
            if (!trackChartEvent) return;
            const prev = new Set(prevValues as Array<string | number>);
            const next = new Set(nextValues as Array<string | number>);
            const added = [...next].filter((v) => !prev.has(v));
            const removed = [...prev].filter((v) => !next.has(v));
            if (added.length > 0) {
                trackChartEvent({
                    type: 'filter_select',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: added,
                });
            }
            if (removed.length > 0) {
                trackChartEvent({
                    type: 'filter_deselect',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: removed,
                });
            }
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const trackFilterDropdownOpen = useCallback(
        (filter_name: string) => {
            trackChartEvent?.({
                type: 'filter_dropdown_open',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                filter_name,
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleSliderChange = useCallback(
        (val: number | number[]) => {
            if (!Array.isArray(val)) return;
            const [start, end] = val as [number, number];
            setSliderRange([start, end]);
            setScrollOffset(0);
            scrollOffsetRef.current = 0;
            trackChartEvent?.({
                type: 'slider_use',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                range: [start, end],
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleChartReady = useCallback((chart: Chart) => {
        chartRef.current = chart;
        scrollOffsetRef.current = 0;
    }, []);

    useEffect(() => {
        if (scrollOffsetRef.current > maxScrollOffset) {
            scrollOffsetRef.current = maxScrollOffset;
            setScrollOffset(maxScrollOffset);
        }
    }, [maxScrollOffset]);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            if (!fixedSpacingV2 && !chartRef.current) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: direction > 0 ? 'forward' : 'backward',
            });
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = dx > 0 ? 1 : -1;
                scrollChart(direction);
                trackChartEvent?.({
                    type: 'scroll',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    direction: direction > 0 ? 'forward' : 'backward',
                });
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, fixedSpacingV2, scrollChart, trackChartEvent, chartTitleForTracking, chartTypeForTracking]);

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-enhanced-linechart
                data-asper-enhanced-linechart-v2={fixedSpacingV2 || undefined}
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section */}
                {showFilters && (filters.length > 0 || (vizData && chartFilters.filterOptions)) && (
                    <div className="px-5 pt-5 pb-3">
                        <ChartFilterBar>
                            {/* Custom filters from props (legacy support) */}
                            {filters.map((filter, index: number) => {
                                const filterLabel =
                                    typeof filter.label === 'string' ? filter.label : `filter-${index}`;
                                return (
                                    <ChartFilterColumn key={`custom-${index}`} label={filter.label}>
                                        <Select
                                            value={filter.defaultValue}
                                            onChange={(value) => {
                                                trackFilterValueChange(
                                                    filterLabel,
                                                    filter.defaultValue != null ? [filter.defaultValue] : [],
                                                    value != null ? [value] : [],
                                                );
                                                filter.onChange?.(value);
                                            }}
                                            onDropdownVisibleChange={(open) =>
                                                open && trackFilterDropdownOpen(filterLabel)
                                            }
                                            options={filter.options}
                                            className={CHART_FILTER_SELECT_CLASSNAME}
                                            data-test-id={`${props['data-test-id']}-filter-${index}`}
                                        />
                                    </ChartFilterColumn>
                                );
                            })}

                            {/* Auto-generated filters from vizData */}
                            {vizData && chartFilters.filterOptions && (
                                <>
                                    {/* X-axis filter */}
                                    {chartFilters.filterOptions.x.length > 0 && (
                                        <ChartFilterColumn label={vizData.xAxisTitle[0] || 'X-Axis'}>
                                            <Select
                                                mode="multiple"
                                                value={chartFilters.filters.x}
                                                onDropdownVisibleChange={(open) =>
                                                    open && trackFilterDropdownOpen(vizData.xAxisTitle[0] || 'X-Axis')
                                                }
                                                onChange={(value) => {
                                                    trackFilterValueChange(
                                                        vizData.xAxisTitle[0] || 'X-Axis',
                                                        chartFilters.filters.x,
                                                        value || [],
                                                    );
                                                    chartFilters.updateFilter('x', value);
                                                }}
                                                options={chartFilters.filterOptions.x.map((x: string) => ({
                                                    label: x,
                                                    value: x,
                                                }))}
                                                tagRender={chartMultiSelectTagRender(chartFilters.filters.x, (v) => v)}
                                                className={CHART_FILTER_SELECT_CLASSNAME}
                                                placeholder="Select..."
                                                data-test-id={`${props['data-test-id']}-filter-x`}
                                                dropdownRender={(menu) =>
                                                    chartFilterDropdownRender(menu, {
                                                        onReset: () => {
                                                            trackFilterValueChange(
                                                                vizData.xAxisTitle[0] || 'X-Axis',
                                                                chartFilters.filters.x,
                                                                [],
                                                            );
                                                            chartFilters.updateFilter('x', []);
                                                        },
                                                        onSelectAll: () => {
                                                            const allX = chartFilters.filterOptions!.x;
                                                            trackFilterValueChange(
                                                                vizData.xAxisTitle[0] || 'X-Axis',
                                                                chartFilters.filters.x,
                                                                allX,
                                                            );
                                                            chartFilters.updateFilter('x', allX);
                                                        },
                                                        dataTestIdPrefix: `${props['data-test-id']}-filter-x`,
                                                    })
                                                }
                                            />
                                        </ChartFilterColumn>
                                    )}

                                    {/* Y1 filter (left) - always show if there are y options */}
                                    {chartFilters.filterOptions.y.length > 0 &&
                                        (() => {
                                            const y1GroupedOptions = buildY1GroupedOptions(
                                                chartFilters.filterOptions.y,
                                                chartFilters.filters.y1,
                                                chartFilters.filters.y2,
                                            );
                                            return (
                                                <ChartFilterColumn label="Primary Axis">
                                                    <Select
                                                        mode="multiple"
                                                        value={chartFilters.filters.y1}
                                                        onDropdownVisibleChange={(open) =>
                                                            open && trackFilterDropdownOpen('Primary Axis')
                                                        }
                                                        onChange={(value) => {
                                                            const filteredValue = (value || []).filter(
                                                                (v: string) => !v.startsWith('__separator_'),
                                                            );
                                                            trackFilterValueChange(
                                                                'Primary Axis',
                                                                chartFilters.filters.y1,
                                                                filteredValue,
                                                            );
                                                            chartFilters.updateFilter('y1', filteredValue);
                                                        }}
                                                        options={y1GroupedOptions}
                                                        tagRender={chartMultiSelectTagRender(
                                                            chartFilters.filters.y1,
                                                            (v) =>
                                                                chartFilters.filterOptions!.y.find((y) => y.value === v)
                                                                    ?.label ?? v,
                                                        )}
                                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                                        placeholder="Select metrics..."
                                                        data-test-id={`${props['data-test-id']}-filter-y1`}
                                                        dropdownRender={(menu) =>
                                                            chartFilterDropdownRender(
                                                                menu,
                                                                {
                                                                    onReset: () => {
                                                                        trackFilterValueChange(
                                                                            'Primary Axis',
                                                                            chartFilters.filters.y1,
                                                                            [],
                                                                        );
                                                                        chartFilters.updateFilter('y1', []);
                                                                    },
                                                                    onSelectAll: () => {
                                                                        const allValues = getYAxisSelectAllValues(
                                                                            y1GroupedOptions,
                                                                            chartFilters.filters.y1,
                                                                        );
                                                                        trackFilterValueChange(
                                                                            'Primary Axis',
                                                                            chartFilters.filters.y1,
                                                                            allValues,
                                                                        );
                                                                        chartFilters.updateFilter('y1', allValues);
                                                                    },
                                                                    dataTestIdPrefix: `${props['data-test-id']}-filter-y1`,
                                                                },
                                                                { includeYAxisSeparatorStyles: true },
                                                            )
                                                        }
                                                    />
                                                </ChartFilterColumn>
                                            );
                                        })()}

                                    {/* Y2 filter (right) — only when multiple metric families exist */}
                                    {showSecondaryAxisFilter &&
                                        (() => {
                                            const y2GroupedOptions = buildY2GroupedOptions(
                                                chartFilters.filterOptions.y,
                                                chartFilters.filters.y1,
                                                chartFilters.filters.y2,
                                            );
                                            return (
                                                <ChartFilterColumn label="Secondary Axis">
                                                    <Select
                                                        mode="multiple"
                                                        value={chartFilters.filters.y2}
                                                        onDropdownVisibleChange={(open) =>
                                                            open && trackFilterDropdownOpen('Secondary Axis')
                                                        }
                                                        onChange={(value) => {
                                                            const filteredValue = (value || []).filter(
                                                                (v: string) => !v.startsWith('__separator_'),
                                                            );
                                                            trackFilterValueChange(
                                                                'Secondary Axis',
                                                                chartFilters.filters.y2,
                                                                filteredValue,
                                                            );
                                                            chartFilters.updateFilter('y2', filteredValue);
                                                        }}
                                                        options={y2GroupedOptions}
                                                        tagRender={chartMultiSelectTagRender(
                                                            chartFilters.filters.y2,
                                                            (v) =>
                                                                chartFilters.filterOptions!.y.find((y) => y.value === v)
                                                                    ?.label ?? v,
                                                        )}
                                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                                        placeholder="Select metrics..."
                                                        data-test-id={`${props['data-test-id']}-filter-y2`}
                                                        dropdownRender={(menu) =>
                                                            chartFilterDropdownRender(
                                                                menu,
                                                                {
                                                                    onReset: () => {
                                                                        trackFilterValueChange(
                                                                            'Secondary Axis',
                                                                            chartFilters.filters.y2,
                                                                            [],
                                                                        );
                                                                        chartFilters.updateFilter('y2', []);
                                                                    },
                                                                    onSelectAll: () => {
                                                                        const allValues = getYAxisSelectAllValues(
                                                                            y2GroupedOptions,
                                                                            chartFilters.filters.y2,
                                                                        );
                                                                        trackFilterValueChange(
                                                                            'Secondary Axis',
                                                                            chartFilters.filters.y2,
                                                                            allValues,
                                                                        );
                                                                        chartFilters.updateFilter('y2', allValues);
                                                                    },
                                                                    dataTestIdPrefix: `${props['data-test-id']}-filter-y2`,
                                                                },
                                                                { includeYAxisSeparatorStyles: true },
                                                            )
                                                        }
                                                    />
                                                </ChartFilterColumn>
                                            );
                                        })()}

                                    {/* Z-axis filters - Z1, Z2, Z3, etc. (similar to Y1, Y2) */}
                                    {chartFilters.filterOptions.z.length > 0 &&
                                        vizData.zAxisTitle &&
                                        vizData.zAxisTitle.map((zTitle, zIndex) => {
                                            const zOpts = chartFilters.zFilterOptions?.[zIndex];
                                            const zSelected = chartFilters.filters.z[zIndex] || [];
                                            const allZ = isZLevelAllSelected(zOpts, zSelected);
                                            const firstZ = getFirstEnabledZOptionValue(zOpts);
                                            return (
                                            <ChartFilterColumn key={`z-${zIndex}`} label={zTitle || `Z${zIndex + 1}`}>
                                                <Select
                                                    mode="multiple"
                                                    value={zSelected}
                                                    onDropdownVisibleChange={(open) =>
                                                        open &&
                                                        trackFilterDropdownOpen(zTitle || `Z${zIndex + 1}`)
                                                    }
                                                    onChange={(value) => {
                                                        trackFilterValueChange(
                                                            zTitle || `Z${zIndex + 1}`,
                                                            zSelected,
                                                            value || [],
                                                        );
                                                        chartFilters.updateFilter('z', value, zIndex);
                                                    }}
                                                    options={zOpts?.map((z) => ({
                                                        label: z.label,
                                                        value: z.value,
                                                        disabled: z.disabled,
                                                    })) || []}
                                                    tagRender={zAxisSelectTagRender(
                                                        zSelected,
                                                        allZ,
                                                        firstZ,
                                                        (v) => zOpts?.find((z) => z.value === v)?.label ?? v,
                                                    )}
                                                    className={CHART_FILTER_SELECT_CLASSNAME}
                                                    placeholder="Select..."
                                                    data-test-id={`${props['data-test-id']}-filter-z${zIndex + 1}`}
                                                    dropdownRender={(menu) =>
                                                        chartFilterDropdownRender(menu, {
                                                            onReset: () => {
                                                                trackFilterValueChange(
                                                                    zTitle || `Z${zIndex + 1}`,
                                                                    zSelected,
                                                                    [],
                                                                );
                                                                chartFilters.updateFilter('z', [], zIndex);
                                                            },
                                                            onSelectAll: () => {
                                                                const allValues = getSelectableOptionValues(
                                                                    zOpts ?? [],
                                                                );
                                                                trackFilterValueChange(
                                                                    zTitle || `Z${zIndex + 1}`,
                                                                    zSelected,
                                                                    allValues,
                                                                );
                                                                chartFilters.updateFilter('z', allValues, zIndex);
                                                            },
                                                            dataTestIdPrefix: `${props['data-test-id']}-filter-z${zIndex + 1}`,
                                                        })
                                                    }
                                                />
                                            </ChartFilterColumn>
                                            );
                                        })}
                                </>
                            )}
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend — Y × Z series labels with horizontal overflow */}
                {effectiveLegendItems.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <ChartLegendOverflow
                            items={effectiveLegendItems}
                            data-test-id={props['data-test-id'] ? `${props['data-test-id']}-legend` : undefined}
                        />
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisLabel && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisLabel}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    {data.length === 0 || (y1LineConfigs.length === 0 && y2LineConfigs.length === 0) ? (
                        <ChartNoDataState
                            minHeight={400}
                            data-test-id={`${props['data-test-id']}-no-data`}
                        />
                    ) : (
                        <LineChart
                        data={transformedData}
                        linesConfig={linesConfig}
                        onChartReady={showScrollbar && !fixedSpacingV2 ? handleChartReady : undefined}
                        sizeAttributes={{
                            height: 400,
                            // V2 wraps x-axis ticks across up to three lines; reserve extra bottom room so the
                            // third line is never clipped (non-V2 keeps its original single-line padding).
                            paddingBottom: showSlider && !showScrollbar ? 100 : fixedSpacingV2 ? 88 : 60,
                            // Reserve space for Y2 axis labels to prevent overlap when there's lots of data
                            ...(y2LineConfigs.length > 0 && {
                                insetRight:
                                    new Set(data.map((d: any) => d[xAxisKey])).size > 20 ? 65 : 55,
                            }),
                            ...sizeAttributes,
                        }}
                        viewLevelAdditionalProps={{
                            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
                            axis: {
                                x: {
                                    title: false,
                                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                                    // Same contract as the mark-level axis config above.
                                    transform: getCategoryAxisLabelTransforms(
                                        fixedSpacingV2 ? { tickSpacePx: LINE_FIXED_CATEGORY_STRIDE_PX } : {},
                                    ),
                                },
                                y: {
                                    labelFormatter: (value: number) => formatNumWithKMB(value),
                                    title: false,
                                    grid: true,
                                    gridLineWidth: 1,
                                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                                },
                            },
                            legend: false, // Hide default legend since we provide custom legend
                            ...viewLevelAdditionalProps,
                            interaction: {
                                ...getChartColumnHighlightInteraction(),
                                ...viewLevelAdditionalProps?.interaction,
                                tooltip: {
                                    ...getAggregatedChartTooltipInteraction(),
                                    ...viewLevelAdditionalProps?.interaction?.tooltip,
                                },
                                /**
                                 * G2's `sliderFilter` / `scrollbarFilter` interactions default to
                                 * `adaptiveMode: 'filter'`, which on every scroll/slider change
                                 * recomputes the value-axis domain from the data currently visible
                                 * inside the scroll window — silently overriding the explicit
                                 * `scale.y.domain` set on the line marks. The result is that the
                                 * y-scale "jumps" each time the user drags the scrollbar/slider.
                                 *
                                 * Disabling adaptive mode on both interactions makes scrolling a
                                 * pure pan operation: the data window changes, but the value-axis
                                 * scale stays pinned to the full-dataset domain we computed.
                                 */
                                sliderFilter: {
                                    ...((viewLevelAdditionalProps?.interaction as any)?.sliderFilter ?? {}),
                                    adaptiveMode: false,
                                },
                                scrollbarFilter: {
                                    ...((viewLevelAdditionalProps?.interaction as any)?.scrollbarFilter ?? {}),
                                    adaptiveMode: false,
                                },
                            },
                            clip: false,
                        }}
                        additionalMarks={additionalMarks}
                        pointClickHandler={onPointClick}
                        data-test={`${props['data-test']}-chart`}
                    />
                    )}
                </div>

                {fixedSpacingV2 && needsScroll && (
                    <div className="px-5 pb-4 flex justify-center" data-export-exclude>
                        <div
                            ref={scrollbarTrackRef}
                            role="scrollbar"
                            aria-orientation="horizontal"
                            aria-valuemin={0}
                            aria-valuemax={maxScrollOffset}
                            aria-valuenow={effectiveScrollOffset}
                            tabIndex={0}
                            className="relative h-2 rounded-full cursor-pointer select-none touch-none"
                            style={{
                                width: '38%',
                                minWidth: 112,
                                maxWidth: 360,
                                background: '#f0f0f0',
                            }}
                            onPointerDown={handleScrollbarPointerDown}
                            onPointerMove={handleScrollbarPointerMove}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowLeft') {
                                    event.preventDefault();
                                    scrollChart(-1);
                                } else if (event.key === 'ArrowRight') {
                                    event.preventDefault();
                                    scrollChart(1);
                                }
                            }}
                            data-test-id={`${props['data-test-id']}-v2-scroller`}
                        >
                            <div
                                className="absolute top-0 h-2 rounded-full"
                                style={{
                                    left: `${scrollbarThumbLeftPercent}%`,
                                    width: `${scrollbarThumbWidthPercent}%`,
                                    background: '#5B50D1',
                                    opacity: 0.45,
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Range Slider - filters which categories are included from the full dataset (scrollbar mode) */}
                {!fixedSpacingV2 && showScrollbar && showSlider && data.length > 0 && totalCategories > 1 && (
                    <div className="px-5 pb-1" data-export-exclude>
                        <Slider
                            range
                            min={0}
                            max={totalCategories - 1}
                            value={sliderRange}
                            onChange={handleSliderChange}
                            tooltip={{
                                formatter: (val?: number) =>
                                    val !== undefined && uniqueCategories[val] ? String(uniqueCategories[val]) : '',
                            }}
                            styles={{
                                track: { background: '#5B50D1' },
                                rail: { background: '#f0f0f0' },
                            }}
                            data-test-id={`${props['data-test-id']}-scroll-slider`}
                        />
                    </div>
                )}

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default EnhancedLineChart;
